import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { History } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../../api/analytics';
import { HistorySnapshotValue, InstanceHistoryRecord } from '../../../types/analytics';
import { formatDate, formatDateTime } from '../../../utils';

type InstanceHistoryTimelineProps = {
  modelName: string;
  objectId: string | number;
  refreshEventName?: string;
};

type ChangeType = 'added' | 'removed' | 'updated';

type HistoryChange = {
  path: string;
  before: HistorySnapshotValue | undefined;
  after: HistorySnapshotValue | undefined;
  displayBefore?: HistorySnapshotValue | undefined;
  displayAfter?: HistorySnapshotValue | undefined;
  type: ChangeType;
};

type HistoryValueLabels = {
  emptyLabel: string;
  yesLabel: string;
  noLabel: string;
};

type TranslateHistoryLabel = (label: string) => string;

type HistoryDisplayValues = {
  displayBefore?: HistorySnapshotValue | undefined;
  displayAfter?: HistorySnapshotValue | undefined;
};

type FormatHistoryValueOptions = {
  translatePrimitiveStrings?: boolean;
};

const DISPLAY_FIELD_SUFFIX = '_display';

// Snapshot values come straight from the backend's JSON history payload (Python
// `date`/`datetime.isoformat()`), not through any app-specific date field
// handling, so raw ISO strings need to be detected and reformatted here to
// match the app's standard "YYYY-MM-DD HH:MM" / "DD-MM-YYYY" display.
const ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const formatHistoryPrimitiveString = (value: string): string => {
  if (ISO_DATETIME_REGEX.test(value)) {
    return formatDateTime(value).replace('\n', ' ');
  }

  if (ISO_DATE_REGEX.test(value)) {
    return formatDate(value);
  }

  return value;
};

const isPlainObject = (
  value: HistorySnapshotValue | undefined
): value is { [key: string]: HistorySnapshotValue } => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

const isDisplayFieldKey = (key: string): boolean => key.endsWith(DISPLAY_FIELD_SUFFIX);

const getDisplayFieldKey = (key: string): string => `${key}${DISPLAY_FIELD_SUFFIX}`;

const parseJsonString = (value: string): HistorySnapshotValue | undefined => {
  const trimmedValue = value.trim();

  if (!trimmedValue || !['{', '['].includes(trimmedValue[0])) {
    return undefined;
  }

  try {
    const parsedValue = JSON.parse(trimmedValue);

    if (parsedValue === null || typeof parsedValue !== 'object') {
      return undefined;
    }

    return parsedValue as HistorySnapshotValue;
  } catch {
    return undefined;
  }
};

const normalizeHistoryValue = (
  value: HistorySnapshotValue | undefined
): HistorySnapshotValue | undefined => {
  if (typeof value !== 'string') {
    return value;
  }

  return parseJsonString(value) ?? value;
};

const valuesAreEqual = (
  left: HistorySnapshotValue | undefined,
  right: HistorySnapshotValue | undefined
): boolean => {
  const normalizedLeft = normalizeHistoryValue(left);
  const normalizedRight = normalizeHistoryValue(right);

  if (normalizedLeft === normalizedRight) {
    return true;
  }

  left = normalizedLeft;
  right = normalizedRight;

  if (left === right) {
    return true;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    const leftArray = left;
    const rightArray = right;

    if (leftArray.length !== rightArray.length) {
      return false;
    }

    return leftArray.every((item, index) => valuesAreEqual(item, rightArray[index]));
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const leftObject = left;
    const rightObject = right;
    const leftKeys = Object.keys(leftObject).sort();
    const rightKeys = Object.keys(rightObject).sort();

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    return leftKeys.every((key, index) => (
      key === rightKeys[index] && valuesAreEqual(leftObject[key], rightObject[key])
    ));
  }

  return false;
};

const isStructuredHistoryValue = (value: HistorySnapshotValue | undefined): boolean => {
  const normalizedValue = normalizeHistoryValue(value);

  return Array.isArray(normalizedValue) || isPlainObject(normalizedValue);
};

const shouldDiffArrayItems = (
  before: HistorySnapshotValue[],
  after: HistorySnapshotValue[]
): boolean => {
  return [...before, ...after].some((item) => isStructuredHistoryValue(item));
};

const joinPath = (segments: string[]): string => {
  if (segments.length === 0) {
    return 'root';
  }

  return segments.reduce((path, segment) => {
    if (segment.startsWith('[')) {
      return `${path}${segment}`;
    }

    return path ? `${path}.${segment}` : segment;
  }, '');
};

const createHistoryChange = (
  pathSegments: string[],
  before: HistorySnapshotValue | undefined,
  after: HistorySnapshotValue | undefined,
  type: ChangeType,
  displayValues: HistoryDisplayValues = {}
): HistoryChange => ({
  path: joinPath(pathSegments),
  before,
  after,
  displayBefore: displayValues.displayBefore,
  displayAfter: displayValues.displayAfter,
  type,
});

export const diffValues = (
  before: HistorySnapshotValue | undefined,
  after: HistorySnapshotValue | undefined,
  pathSegments: string[] = [],
  displayValues: HistoryDisplayValues = {}
): HistoryChange[] => {
  before = normalizeHistoryValue(before);
  after = normalizeHistoryValue(after);

  if (valuesAreEqual(before, after)) {
    return [];
  }

  if (typeof before === 'undefined') {
    return [createHistoryChange(pathSegments, before, after, 'added', displayValues)];
  }

  if (typeof after === 'undefined') {
    return [createHistoryChange(pathSegments, before, after, 'removed', displayValues)];
  }

  if (Array.isArray(before) && Array.isArray(after)) {
    const beforeArray = before;
    const afterArray = after;

    if (!shouldDiffArrayItems(beforeArray, afterArray)) {
      return [createHistoryChange(pathSegments, beforeArray, afterArray, 'updated', displayValues)];
    }

    const itemCount = Math.max(beforeArray.length, afterArray.length);

    return Array.from({ length: itemCount }).flatMap((_, index) => (
      diffValues(beforeArray[index], afterArray[index], [...pathSegments, `[${index}]`])
    ));
  }

  if (isPlainObject(before) && isPlainObject(after)) {
    const beforeObject = before;
    const afterObject = after;
    const keys = Array.from(new Set([...Object.keys(beforeObject), ...Object.keys(afterObject)]))
      .filter((key) => !isDisplayFieldKey(key))
      .sort();

    return keys.flatMap((key) => diffValues(
      beforeObject[key],
      afterObject[key],
      [...pathSegments, key],
      {
        displayBefore: beforeObject[getDisplayFieldKey(key)],
        displayAfter: afterObject[getDisplayFieldKey(key)],
      }
    ));
  }

  return [createHistoryChange(pathSegments, before, after, 'updated', displayValues)];
};

const humanizeHistorySegment = (
  segment: string,
  translateLabel: TranslateHistoryLabel = (label) => label
): string => {
  const indexMatches = segment.match(/\[(\d+)\]/g) ?? [];
  const baseSegment = segment.replace(/\[(\d+)\]/g, '');
  const normalizedBaseSegment = ['_display', '_ids', '_id'].reduce((value, suffix) => (
    value.endsWith(suffix) ? value.slice(0, -suffix.length) : value
  ), baseSegment);
  const readableBaseSegment = normalizedBaseSegment
    ? normalizedBaseSegment
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (character) => character.toUpperCase())
    : '';
  const translatedBaseSegment = translateLabel(readableBaseSegment);
  const readableIndexes = indexMatches.map((match) => Number(match.slice(1, -1)) + 1);

  return [translatedBaseSegment, ...readableIndexes].filter(Boolean).join(' / ');
};

const humanizeHistoryPath = (
  path: string,
  translateLabel: TranslateHistoryLabel = (label) => label
): string => {
  if (path === 'root') {
    return translateLabel('Root');
  }

  return path
    .split('.')
    .map((segment) => humanizeHistorySegment(segment, translateLabel))
    .filter(Boolean)
    .join(' / ');
};

const indentMultilineValue = (value: string): string => {
  return value
    .split('\n')
    .map((line, index) => (index === 0 ? line : `  ${line}`))
    .join('\n');
};

const formatLabeledHistoryValue = (label: string, value: string): string => {
  if (value.includes('\n')) {
    return `${label}:\n${indentMultilineValue(value)}`;
  }

  return `${label}: ${value}`;
};

export const formatHistoryValue = (
  value: HistorySnapshotValue | undefined,
  labels: HistoryValueLabels,
  translateLabel: TranslateHistoryLabel = (label) => label,
  options: FormatHistoryValueOptions = {}
): string => {
  const { translatePrimitiveStrings = false } = options;
  const normalizedValue = normalizeHistoryValue(value);

  if (typeof normalizedValue === 'undefined' || normalizedValue === null || normalizedValue === '') {
    return labels.emptyLabel;
  }

  if (typeof normalizedValue === 'boolean') {
    return normalizedValue ? labels.yesLabel : labels.noLabel;
  }

  if (typeof normalizedValue === 'number' || typeof normalizedValue === 'string') {
    if (typeof normalizedValue === 'string' && translatePrimitiveStrings) {
      return translateLabel(normalizedValue);
    }

    if (typeof normalizedValue === 'string') {
      return formatHistoryPrimitiveString(normalizedValue);
    }

    return String(normalizedValue);
  }

  if (Array.isArray(normalizedValue)) {
    if (normalizedValue.length === 0) {
      return labels.emptyLabel;
    }

    return normalizedValue
      .map((item, index) => formatLabeledHistoryValue(
        String(index + 1),
        formatHistoryValue(item, labels, translateLabel, options)
      ))
      .join('\n');
  }

  const objectEntries = Object.entries(normalizedValue);

  if (objectEntries.length === 0) {
    return labels.emptyLabel;
  }

  const rawEntries = objectEntries.filter(([key]) => !isDisplayFieldKey(key));

  return rawEntries
    .map(([key, nestedValue]) => {
      const displayValue = normalizedValue[getDisplayFieldKey(key)];
      const resolvedValue = typeof displayValue === 'undefined' ? nestedValue : displayValue;

      return formatLabeledHistoryValue(
        humanizeHistorySegment(key, translateLabel),
        formatHistoryValue(resolvedValue, labels, translateLabel, {
          translatePrimitiveStrings: typeof displayValue !== 'undefined',
        })
      );
    })
    .join('\n');
};

export const formatHistoryChangeValue = (
  value: HistorySnapshotValue | undefined,
  displayValue: HistorySnapshotValue | undefined,
  labels: HistoryValueLabels,
  translateLabel: TranslateHistoryLabel = (label) => label
): string => {
  return formatHistoryValue(
    typeof displayValue === 'undefined' ? value : displayValue,
    labels,
    translateLabel,
    {
      translatePrimitiveStrings: typeof displayValue !== 'undefined',
    }
  );
};

const getChangeChipColor = (type: ChangeType): 'success' | 'error' | 'warning' => {
  if (type === 'added') {
    return 'success';
  }

  if (type === 'removed') {
    return 'error';
  }

  return 'warning';
};

const InstanceHistoryTimelineSkeleton = () => (
  <Stack spacing={0}>
    {Array.from({ length: 3 }).map((_, index) => (
      <Box key={index} sx={{ position: 'relative', pl: { xs: 4, md: 5 }, pb: 3 }}>
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 8, md: 12 },
            top: 6,
            bottom: -12,
            width: 2,
            bgcolor: 'divider',
          }}
        />
        <Skeleton
          variant="circular"
          width={24}
          height={24}
          sx={{ position: 'absolute', left: 0, top: 0 }}
        />
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              backgroundColor: 'action.hover',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="text" width={140} height={20} />
            </Box>
          </Box>
          <Divider />
          <Stack spacing={1.5} sx={{ p: 2 }}>
            {Array.from({ length: 2 }).map((__, changeIndex) => (
              <Fragment key={changeIndex}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Skeleton variant="rounded" width={88} height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="75%" height={22} />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1,
                    }}
                  >
                    <Skeleton variant="rounded" height={76} />
                    <Skeleton variant="rounded" height={76} />
                  </Box>
                </Box>
                {changeIndex < 1 && <Divider flexItem />}
              </Fragment>
            ))}
          </Stack>
        </Paper>
      </Box>
    ))}
  </Stack>
);

const HistoryRecordCard = ({ record }: { record: InstanceHistoryRecord }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const translateHistoryLabel = useCallback((label: string) => (label ? t(label) : label), [t]);
  const historyValueLabels: HistoryValueLabels = {
    emptyLabel: t('Empty'),
    // Values are plain text here (nested in lists and "label: value" lines),
    // so the tick and cross shown for booleans elsewhere are characters.
    yesLabel: '✓',
    noLabel: '✗',
  };

  const changes = useMemo(
    () => diffValues(record.state_before, record.state_after),
    [record.state_after, record.state_before]
  );

  const neutralPanelBackground = alpha(
    theme.palette.text.primary,
    theme.palette.mode === 'dark' ? 0.12 : 0.05
  );

  const neutralPanelBorder = alpha(
    theme.palette.text.primary,
    theme.palette.mode === 'dark' ? 0.22 : 0.1
  );

  const successPanelBackground = alpha(
    theme.palette.success.main,
    theme.palette.mode === 'dark' ? 0.18 : 0.1
  );

  const successPanelBorder = alpha(
    theme.palette.success.main,
    theme.palette.mode === 'dark' ? 0.3 : 0.18
  );

  return (
    <Box sx={{ position: 'relative', pl: { xs: 4, md: 5 }, pb: 3 }}>
      <Box
        sx={{
          position: 'absolute',
          left: { xs: 8, md: 12 },
          top: 6,
          bottom: -12,
          width: 2,
          bgcolor: 'divider',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 2,
        }}
      >
        <History sx={{ fontSize: 14 }} />
      </Box>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            backgroundColor: 'action.hover',
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {formatDateTime(record.timestamp)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Changed by')}: {record.user_full_name || t('System')}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Stack spacing={1.5} sx={{ p: 2 }}>
          {changes.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('No field changes detected')}
            </Typography>
          )}
          {changes.map((change, index) => (
            <Fragment key={`${change.path}-${index}`}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
                  gap: 1.5,
                }}
              >
                <Box>
                  <Chip
                    size="small"
                    color={getChangeChipColor(change.type)}
                    label={t(
                      change.type === 'added'
                        ? 'Added'
                        : change.type === 'removed'
                          ? 'Removed'
                          : 'Updated'
                    )}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {humanizeHistoryPath(change.path, translateHistoryLabel)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      backgroundColor: neutralPanelBackground,
                      border: '1px solid',
                      borderColor: neutralPanelBorder,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('From value')}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {formatHistoryChangeValue(
                        change.before,
                        change.displayBefore,
                        historyValueLabels,
                        translateHistoryLabel
                      )}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      backgroundColor: successPanelBackground,
                      border: '1px solid',
                      borderColor: successPanelBorder,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('To value')}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {formatHistoryChangeValue(
                        change.after,
                        change.displayAfter,
                        historyValueLabels,
                        translateHistoryLabel
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {index < changes.length - 1 && <Divider flexItem />}
            </Fragment>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export const InstanceHistoryTimeline = ({
  modelName,
  objectId,
  refreshEventName,
}: InstanceHistoryTimelineProps) => {
  const { t } = useTranslation();
  const { loading, error, getInstanceHistory } = useAnalytics();
  const [records, setRecords] = useState<InstanceHistoryRecord[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const loadHistoryPage = useCallback(async (
    page: number,
    options?: { append?: boolean }
  ) => {
    const response = await getInstanceHistory(modelName, objectId, page);
    if (!response) {
      return;
    }

    setRecords((currentRecords) => (
      options?.append ? [...currentRecords, ...response.results] : response.results
    ));
    setCurrentPage(page);
    setHasNextPage(Boolean(response.next));
  }, [getInstanceHistory, modelName, objectId]);

  const refreshHistory = useCallback(async () => {
    setIsInitialLoading(true);
    setRecords([]);
    setCurrentPage(1);
    setHasNextPage(false);
    try {
      await loadHistoryPage(1);
    } finally {
      setIsInitialLoading(false);
    }
  }, [loadHistoryPage]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialHistory = async () => {
      setIsInitialLoading(true);

      try {
        const response = await getInstanceHistory(modelName, objectId, 1);
        if (!isMounted || !response) {
          return;
        }

        setRecords(response.results);
        setCurrentPage(1);
        setHasNextPage(Boolean(response.next));
      } finally {
        if (isMounted) {
          setIsInitialLoading(false);
        }
      }
    };

    setRecords([]);
    setCurrentPage(1);
    setHasNextPage(false);
    loadInitialHistory();

    return () => {
      isMounted = false;
    };
  }, [getInstanceHistory, modelName, objectId]);

  useEffect(() => {
    if (!refreshEventName) {
      return undefined;
    }

    const handleRefreshEvent = () => {
      refreshHistory();
    };

    window.addEventListener(refreshEventName, handleRefreshEvent);

    return () => {
      window.removeEventListener(refreshEventName, handleRefreshEvent);
    };
  }, [refreshEventName, refreshHistory]);

  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    await loadHistoryPage(nextPage, { append: true });
  };

  if (isInitialLoading) {
    return <InstanceHistoryTimelineSkeleton />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('Failed to load history')}
        </Alert>
      )}

      {!isInitialLoading && !loading && records.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary">
          {t('No history records available')}
        </Typography>
      )}

      {records.length > 0 && (
        <Stack spacing={0}>
          {records.map((record) => (
            <HistoryRecordCard key={record.id} record={record} />
          ))}
        </Stack>
      )}

      {hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button onClick={handleLoadMore} disabled={loading} variant="outlined">
            {t('Load more')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default InstanceHistoryTimeline;
